import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Form, Modal, Alert, Spinner } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import { fetchYoutubeVideos, createYoutubeVideo, updateYoutubeVideo, deleteYoutubeVideo } from "../services/YoutubeVideoService";
import YoutubeVideoData from "../models/YoutubeVideoData";

const ManageYoutubeVideosPage: React.FC = () => {
  const [videos, setVideos] = useState<YoutubeVideoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState<YoutubeVideoData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    videoUrl: "",
    isActive: true,
    isSpecial: false,
    showFromDate: "",
    showToDate: ""
  });

  useEffect(() => {
    loadVideos();
  }, [page, searchQuery]);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const response = await fetchYoutubeVideos(page, 12, searchQuery);
      setVideos(response.videos);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError("Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadVideos();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleShowModal = (video?: YoutubeVideoData) => {
    if (video) {
      setEditingVideo(video);
      setFormData({
        title: video.title,
        description: video.description || "",
        videoUrl: video.videoUrl,
        isActive: video.isActive,
        isSpecial: video.isSpecial,
        showFromDate: video.showFromDate ? new Date(video.showFromDate).toISOString().split('T')[0] : "",
        showToDate: video.showToDate ? new Date(video.showToDate).toISOString().split('T')[0] : ""
      });
    } else {
      setEditingVideo(null);
      setFormData({
        title: "",
        description: "",
        videoUrl: "",
        isActive: true,
        isSpecial: false,
        showFromDate: "",
        showToDate: ""
      });
    }
    setShowModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const submitData = {
        ...formData,
        showFromDate: formData.showFromDate || null,
        showToDate: formData.showToDate || null
      };

      if (editingVideo) {
        await updateYoutubeVideo(editingVideo._id!, submitData);
        setSuccess("Video updated successfully");
      } else {
        await createYoutubeVideo(submitData);
        setSuccess("Video created successfully");
      }
      
      setShowModal(false);
      loadVideos();
    } catch (err) {
      setError(editingVideo ? "Failed to update video" : "Failed to create video");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (videoId: string) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      try {
        await deleteYoutubeVideo(videoId);
        setSuccess("Video deleted successfully");
        loadVideos();
      } catch (err) {
        setError("Failed to delete video");
      }
    }
  };

  // Extract YouTube video ID from URL
  const getYoutubeVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  return (
    <PermissionWrapper pageName="ManageYoutubeVideosPage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <h2 className="mb-3 mb-md-0">
                <i className="bi bi-youtube me-2 text-danger"></i>
                Manage YouTube Videos
              </h2>
              <Button variant="danger" onClick={() => handleShowModal()}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Video
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <div className="d-flex">
              <Form.Control
                type="text"
                placeholder="Search videos by title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="me-2"
              />
              <Button 
                variant="outline-secondary" 
                onClick={handleSearch}
              >
                <i className="bi bi-search"></i>
              </Button>
            </div>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {loading && videos.length === 0 ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <Row className="g-4">
            {videos.map((video) => {
              const videoId = getYoutubeVideoId(video.videoUrl);
              return (
                <Col key={video._id} lg={3} md={4} sm={6}>
                  <Card className="h-100 shadow-sm">
                    <div className="position-relative">
                      {video.thumbnailUrl ? (
                        <Card.Img 
                          variant="top" 
                          src={video.thumbnailUrl} 
                          alt={video.title}
                          style={{ height: "180px", objectFit: "cover" }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                          }}
                        />
                      ) : (
                        <Card.Img 
                          variant="top" 
                          src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                          alt={video.title}
                          style={{ height: "180px", objectFit: "cover" }}
                        />
                      )}
                      <div 
                        className="position-absolute top-0 end-0 m-2 p-1 rounded-circle" 
                        style={{ backgroundColor: video.isActive ? '#28a745' : '#dc3545', width: '15px', height: '15px' }}
                        title={video.isActive ? 'Active' : 'Inactive'}
                      ></div>
                      {video.isSpecial && (
                        <div 
                          className="position-absolute top-0 start-0 m-2 px-2 py-1 rounded-pill"
                          style={{ backgroundColor: 'rgba(255, 193, 7, 0.8)', fontSize: '0.7rem', fontWeight: 'bold' }}
                        >
                          FEATURED
                        </div>
                      )}
                      <div 
                        className="position-absolute top-50 start-50 translate-middle"
                        style={{ color: 'white', fontSize: '3rem', opacity: 0.8 }}
                      >
                        <i className="bi bi-play-circle-fill"></i>
                      </div>
                    </div>
                    <Card.Body className="d-flex flex-column">
                      <Card.Title className="h6">{video.title}</Card.Title>
                      <Card.Text className="small text-muted mb-2">
                        {video.description && video.description.length > 80 
                          ? `${video.description.substring(0, 80)}...` 
                          : video.description}
                      </Card.Text>
                      <div className="small text-muted mt-auto mb-2">
                        {video.showFromDate && (
                          <div>
                            <i className="bi bi-calendar-check me-1"></i>
                            From: {new Date(video.showFromDate).toLocaleDateString()}
                          </div>
                        )}
                        {video.showToDate && (
                          <div>
                            <i className="bi bi-calendar-x me-1"></i>
                            To: {new Date(video.showToDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <div className="d-flex gap-2 mt-auto">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="flex-grow-1"
                          onClick={() => handleShowModal(video)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(video._id!)}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        {videos.length === 0 && !loading && (
          <div className="text-center py-5">
            <i className="bi bi-film display-1 text-muted"></i>
            <h4 className="mt-3 text-muted">No videos found</h4>
            <p className="text-muted">
              {searchQuery ? `No results for "${searchQuery}"` : "Add your first YouTube video"}
            </p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <Button
              variant="outline-secondary"
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="me-2"
            >
              <i className="bi bi-chevron-left"></i>
            </Button>
            <span className="align-self-center mx-2">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline-secondary"
              disabled={page === totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className="ms-2"
            >
              <i className="bi bi-chevron-right"></i>
            </Button>
          </div>
        )}

        {/* Video Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingVideo ? "Edit Video" : "Add New Video"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Video Title*</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>YouTube Video URL*</Form.Label>
                <Form.Control
                  type="url"
                  name="videoUrl"
                  value={formData.videoUrl}
                  onChange={handleInputChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
                <Form.Text className="text-muted">
                  Enter the full YouTube video URL
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Show From Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="showFromDate"
                      value={formData.showFromDate}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Show To Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="showToDate"
                      value={formData.showToDate}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    label="Active"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Show this video on the website
                  </Form.Text>
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    id="isSpecial"
                    name="isSpecial"
                    label="Special Video"
                    checked={formData.isSpecial}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Mark as a special/featured video
                  </Form.Text>
                </Col>
              </Row>

              {formData.videoUrl && getYoutubeVideoId(formData.videoUrl) && (
                <div className="mt-3">
                  <h6>Preview:</h6>
                  <div className="ratio ratio-16x9">
                    <iframe
                      src={`https://www.youtube.com/embed/${getYoutubeVideoId(formData.videoUrl)}`}
                      title="YouTube video preview"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>{editingVideo ? "Update" : "Add"} Video</>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </PermissionWrapper>
  );
};

export default ManageYoutubeVideosPage;